/**
 * The app's one tab strip: a real `role="tablist"` with ONE tab stop, arrows that move focus, and
 * Enter or Space to choose.
 *
 * Promoted out of `pages/client-detail/ui/ClientDetailScreen.tsx` on 17 September 2026 when
 * `/raporty` became the second consumer. The behaviour is the client detail's, unchanged, and
 * `e2e/client-detail.spec.ts` is what says so.
 *
 * ── The roving stop lives HERE, and that is the whole reason this is a component ─────────────
 * A caller passes which tab is SELECTED and gets back which tab was chosen. Which tab holds the
 * strip's single tab stop is the strip's own business: it follows FOCUS rather than selection, it
 * is adjusted during render when the selection moves from outside — a link, Back, a deep link — so
 * the stop never points at a tab the trainer did not end up on, and it is a piece of state the
 * caller would otherwise have to own twice.
 *
 * What a caller does NOT get to decide: the roles, the ids, the `aria-selected`/`aria-controls`
 * wiring, the key handling, the ink, the weights, the focus ring or the ember rule. What it owns is
 * the bar's box (`className`, see the stylesheet), the words, the order, and what to do when one is
 * chosen.
 *
 * ── Focus from outside ───────────────────────────────────────────────────────────────────────
 * Through `tabButtonId`, not a ref. The client detail's summary column stands on one tab alone, so
 * leaving that tab takes the focused element off the screen and focus falls to `<body>`; the column
 * hands the stop to the tab that was just chosen by looking its button up. One spelling, exported
 * beside the focus rule.
 */

import { useState, type ReactNode } from 'react';

import { tabButtonId, tabFocusTarget, tabPanelId } from './tabFocusTarget';
import styles from './TabStrip.module.css';
import rule from '../tabRule.module.css';

interface TabStripProps<Key extends string> {
  /** The tablist's accessible name. A resolved string: `shared/` may not know a locale namespace. */
  readonly label: string;
  /** The tabs, in the order they are drawn. Also the order the arrows walk. */
  readonly tabs: readonly Key[];
  readonly selected: Key;
  /**
   * The word on a tab — a node rather than a string because a tab may carry a figure beside its
   * word (`Do przejrzenia · 5`), and the figure is the caller's read, not the strip's.
   */
  readonly renderLabel: (tab: Key) => ReactNode;
  readonly onSelect: (tab: Key) => void;
  /**
   * What stands at the right-hand end of the bar, after the strip: the client detail's one line
   * about the tab in view, `/raporty`'s search field and filter button. Outside the tablist, so it
   * is never walked by the strip's arrows.
   */
  readonly trailing?: ReactNode;
  /** The bar's own box — height, padding, gap, ground, hairline. See the stylesheet. */
  readonly className?: string;
  /**
   * The tab's skin. `rule` is the app's word-and-underline tab; `cards` is the Talia's (KANON
   * "Karty zadań", 24 September 2026): equal boxes in one row, the chosen one on the card's ground
   * with a stronger edge and no underline. The behaviour is identical — it is the same tablist.
   */
  readonly variant?: 'rule' | 'cards';
}

export function TabStrip<Key extends string>({
  label,
  tabs,
  selected,
  renderLabel,
  onSelect,
  trailing,
  className,
  variant = 'rule',
}: TabStripProps<Key>) {
  const [focused, setFocused] = useState<Key>(selected);
  const [lastSelected, setLastSelected] = useState<Key>(selected);
  if (lastSelected !== selected) {
    setLastSelected(selected);
    setFocused(selected);
  }

  return (
    <div className={`${String(styles.bar)} ${className ?? ''}`}>
      <div
        className={`${String(styles.tabs)} ${variant === 'cards' ? String(styles.cardTabs) : ''}`}
        role="tablist"
        aria-label={label}
      >
        {tabs.map((entry) => (
          <button
            key={entry}
            type="button"
            role="tab"
            id={tabButtonId(entry)}
            aria-selected={entry === selected}
            aria-controls={tabPanelId(entry)}
            tabIndex={entry === focused ? 0 : -1}
            className={
              variant === 'cards'
                ? `${String(styles.cardTab)} ${entry === selected ? String(styles.cardOn) : ''}`
                : `${String(styles.tab)} ${entry === selected ? String(styles.on) : ''}`
            }
            onClick={() => {
              onSelect(entry);
            }}
            onFocus={() => {
              // A click or a Tab into the strip moves the stop with the trainer, so the next arrow
              // starts from where they actually are.
              setFocused(entry);
            }}
            onKeyDown={(event) => {
              // Arrows and Home/End move focus and nothing else. Enter and Space are NOT handled
              // here on purpose: this is a `<button>`, and the browser already turns both into the
              // click above.
              const next = tabFocusTarget(event.key, entry, tabs);
              if (next === null) return;
              event.preventDefault();
              setFocused(next);
              document.getElementById(tabButtonId(next))?.focus();
            }}
          >
            {renderLabel(entry)}
            {/* The app's one tab underline — `shared/ui/tabRule.module.css` for the treatment and
              * the movement, this module's `.rule` for the offset onto the bar's hairline. The
              * chosen state is a SECOND CLASS on the same element rather than a descendant of
              * `.on`, so the shared module never depends on a class it does not own. */}
            {variant === 'cards' ? null : (
              <i
                className={`${String(rule.rule)} ${String(styles.rule)} ${
                  entry === selected ? String(rule.ruleOn) : ''
                }`}
                aria-hidden="true"
              />
            )}
          </button>
        ))}
      </div>
      {trailing}
    </div>
  );
}
